/**
 * 应付对账工作台 - 全局上下文栏 V2
 *
 * 设计文档: docs/05_workbench_architecture_v2.md §2.1
 *
 * 展示：
 *  - 批次基础信息（批次号/供应商/费用类型/机场/币种）
 *  - 风险与状态信号
 *  - 关键金额摘要（待审 / 已入库 / 本批合计）
 *  - 全局操作按钮：上传账单 / 刷新 / 更多
 */
import React from "react";
import { Tag, Space, Spin, Typography, Tooltip, Button, Popover, Divider, Row, Col, Dropdown } from "antd";
import { NumberOutlined, CheckCircleFilled, CloseCircleFilled, WarningFilled, AppstoreOutlined, BookOutlined, EyeOutlined, FileTextOutlined, InfoCircleOutlined, ReloadOutlined, EditOutlined, UploadOutlined, MoreOutlined } from "@ant-design/icons";
import { useBatchContext } from "../context/BatchContext";
import { getStandardCostTypeLabel } from "../../constants";
import { useAuth } from "../../../../hooks/useAuth";
import "./WorkbenchHeader.css";

const { Text } = Typography;

// 格式化金额
const formatAmount = (amount) => {
    if (amount == null) return "-";
    return Number(amount).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const SignalChip = ({ tone = "neutral", icon, children, muted = false, title }) => (
    <Tooltip title={title}>
        <span className={`workbench-header__signal-chip workbench-header__signal-chip--${tone}${muted ? " workbench-header__signal-chip--muted" : ""}`}>
            {icon}
            <span>{children}</span>
        </span>
    </Tooltip>
);

const ValidationSignals = () => {
    const { validationSummary } = useBatchContext();
    if (!validationSummary) return null;

    const { hasIssues, withWarnings, totalFlights, chargesFailed, validatedAt, failedValidationTaskCount, executionFailedFlights } = validationSummary;
    const validatedText = validatedAt ? `验证于 ${new Date(validatedAt).toLocaleString("zh-CN", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" })}` : undefined;

    const detailContent = validationSummary.taskSummary ? (
        <div style={{ maxWidth: 480, maxHeight: 360, overflow: "auto" }}>
            <div style={{ whiteSpace: "pre-wrap", fontSize: 12, lineHeight: 1.6 }}>{validationSummary.taskSummary}</div>
            {validationSummary.taskFeedback && (
                <>
                    <Divider style={{ margin: "8px 0" }} />
                    <div style={{ whiteSpace: "pre-wrap", fontSize: 12, lineHeight: 1.6, color: "#595959" }}>{validationSummary.taskFeedback}</div>
                </>
            )}
        </div>
    ) : null;

    const chips = [];
    if (hasIssues > 0) {
        chips.push(
            <SignalChip key="validation-issues" tone="danger" icon={<CloseCircleFilled />} title={validatedText}>
                验证警告 {hasIssues}
            </SignalChip>,
        );
    } else if (withWarnings > 0) {
        chips.push(
            <SignalChip key="validation-warnings" tone="warning" icon={<WarningFilled />} title={validatedText}>
                验证提醒 {withWarnings}
            </SignalChip>,
        );
    } else if (totalFlights > 0) {
        chips.push(
            <SignalChip key="validation-passed" tone="success" icon={<CheckCircleFilled />} muted title={validatedText}>
                验证通过 {totalFlights}
            </SignalChip>,
        );
    }

    if (chargesFailed > 0) {
        chips.push(
            <SignalChip key="charges-failed" tone="danger" icon={<CloseCircleFilled />}>
                费用警告 {chargesFailed}
            </SignalChip>,
        );
    }

    if (failedValidationTaskCount > 0) {
        chips.push(
            <SignalChip
                key="validation-task-failed"
                tone="warning"
                icon={<WarningFilled />}
                title={executionFailedFlights ? `影响 ${executionFailedFlights} 个航班；表示验证结论缺失或不可信，不等于金额不通过` : undefined}
            >
                验证执行失败 {failedValidationTaskCount}
            </SignalChip>,
        );
    }

    if (chips.length === 0) return null;

    const content = <span className="workbench-header__validation-signals">{chips}</span>;
    if (!detailContent) return content;

    return (
        <Popover content={detailContent} title="验证详情" placement="bottomLeft" trigger="click">
            {content}
        </Popover>
    );
};

const StatusSignals = () => {
    const { statusSummary, validationSummary, summary, excludeSources } = useBatchContext();
    const terminalCount = statusSummary.completed ?? (statusSummary.confirmed + statusSummary.rejected);
    const unmatchedCount = summary?.unmatched_count || 0;
    const unmatchedAmount = summary?.unmatched_amount || 0;
    const hiddenSourceCount = excludeSources.length;
    const hasRisk = statusSummary.failed > 0
        || statusSummary.blocked > 0
        || unmatchedCount > 0
        || hiddenSourceCount > 0
        || (validationSummary?.hasIssues || 0) > 0
        || (validationSummary?.chargesFailed || 0) > 0
        || (validationSummary?.failedValidationTaskCount || 0) > 0;

    return (
        <div className="workbench-header__signals" aria-label="批次状态与风险">
            <SignalChip muted>共 {statusSummary.total} 张</SignalChip>
            {statusSummary.processing > 0 && <SignalChip tone="processing">处理中 {statusSummary.processing}</SignalChip>}
            {statusSummary.failed > 0 && <SignalChip tone="danger" icon={<CloseCircleFilled />}>失败 {statusSummary.failed}</SignalChip>}
            {statusSummary.blocked > 0 && <SignalChip tone="warning" icon={<WarningFilled />}>受阻 {statusSummary.blocked}</SignalChip>}
            {statusSummary.review > 0 && <SignalChip tone="review">待审核 {statusSummary.review}</SignalChip>}
            {terminalCount > 0 && <SignalChip tone="success" muted>已终结 {terminalCount}</SignalChip>}
            <ValidationSignals />
            {unmatchedCount > 0 && (
                <SignalChip tone="warning" icon={<WarningFilled />} title={`${summary?.currency || ""} ${formatAmount(unmatchedAmount)}`}>
                    未匹配 {unmatchedCount}
                </SignalChip>
            )}
            {hiddenSourceCount > 0 && <SignalChip tone="warning">隐藏来源 {hiddenSourceCount}</SignalChip>}
            {!hasRisk && statusSummary.total > 0 && <SignalChip tone="success" icon={<CheckCircleFilled />} muted>无风险</SignalChip>}
        </div>
    );
};

const AmountMetric = ({ label, amount, currency, color, suffix }) => (
    <span className="workbench-header__amount-metric">
        <span className="workbench-header__amount-label">{label}</span>
        <strong className="workbench-header__amount-value" style={{ color }}>{currency} {formatAmount(amount)}</strong>
        {suffix ? <span className="workbench-header__source-pill-suffix">{suffix}</span> : null}
    </span>
);

// ---- 金额统计行 ----
const StatsRow = () => {
    const { summary, sourceSummary, summaryViewMode } = useBatchContext();

    if (!summary || !sourceSummary) return null;

    const currency = summary.currency || "USD";
    const pendingSummary = sourceSummary.pending_aai || {};
    const committedSummary = sourceSummary.committed_invoice || {};
    const combinedVisibleAmount = (pendingSummary.visible_amount || 0) + (committedSummary.visible_amount || 0);
    const referenceAmount = sourceSummary.reference_amount || summary.reference_amount || 0;
    const hasUnmatched = (summary.unmatched_count || 0) > 0;

    const statsContent = (
        <div style={{ minWidth: 420 }}>
            <Row gutter={[16, 12]}>
                <Col span={12}>
                    <div style={{ marginBottom: 8 }}>
                        <span style={{ color: "#8c8c8c", fontSize: 12 }}>批次汇总模式</span>
                        <div style={{ fontSize: 14, fontWeight: 500 }}>{summaryViewMode || "—"}</div>
                    </div>
                </Col>
                <Col span={12}>
                    <div style={{ marginBottom: 8 }}>
                        <span style={{ color: "#8c8c8c", fontSize: 12 }}>来源账单数量</span>
                        <div style={{ fontSize: 14, fontWeight: 500 }}>{summary.invoice_count || 0} 张</div>
                    </div>
                </Col>
                <Col span={12}>
                    <div style={{ marginBottom: 8 }}>
                        <span style={{ color: "#8c8c8c", fontSize: 12 }}>待审来源</span>
                        <div style={{ fontSize: 14, fontWeight: 500 }}>
                            {summary.pending_invoice_count || 0} 张 / {pendingSummary.slice_count || 0} 片
                        </div>
                    </div>
                </Col>
                <Col span={12}>
                    <div style={{ marginBottom: 8 }}>
                        <span style={{ color: "#8c8c8c", fontSize: 12 }}>已入库来源（当前批次）</span>
                        <div style={{ fontSize: 14, fontWeight: 500 }}>
                            {summary.committed_invoice_count || 0} 张 / {committedSummary.slice_count || 0} 片
                        </div>
                    </div>
                </Col>
                <Col span={12}>
                    <div style={{ marginBottom: 8 }}>
                        <span style={{ color: "#8c8c8c", fontSize: 12 }}>飞机数量</span>
                        <div style={{ fontSize: 14, fontWeight: 500 }}>{summary.aircraft_count || 0} 架</div>
                    </div>
                </Col>
                <Col span={12}>
                    <div style={{ marginBottom: 8 }}>
                        <span style={{ color: "#8c8c8c", fontSize: 12 }}>航班总数</span>
                        <div style={{ fontSize: 14, fontWeight: 500 }}>{summary.flight_count || 0} 个</div>
                    </div>
                </Col>
                <Col span={12}>
                    <div style={{ marginBottom: 8 }}>
                        <span style={{ color: "#8c8c8c", fontSize: 12 }}>有费用航班（当前批次）</span>
                        <div style={{ fontSize: 14, fontWeight: 500 }}>{summary.charged_flight_count || 0} 个</div>
                    </div>
                </Col>
                <Col span={12}>
                    <div style={{ marginBottom: 8 }}>
                        <span style={{ color: "#8c8c8c", fontSize: 12 }}>待审金额（当前批次）</span>
                        <div style={{ fontSize: 14, fontWeight: 500, color: "#1677ff" }}>
                            {currency} {formatAmount(pendingSummary.visible_amount)}
                        </div>
                    </div>
                </Col>
                <Col span={12}>
                    <div style={{ marginBottom: 8 }}>
                        <span style={{ color: "#8c8c8c", fontSize: 12 }}>已入库金额（当前批次）</span>
                        <div style={{ fontSize: 14, fontWeight: 500, color: "#389e0d" }}>
                            {currency} {formatAmount(committedSummary.visible_amount)}
                        </div>
                    </div>
                </Col>
                <Col span={12}>
                    <div style={{ marginBottom: 8 }}>
                        <span style={{ color: "#8c8c8c", fontSize: 12 }}>当前批次合计</span>
                        <div style={{ fontSize: 14, fontWeight: 500, color: "#722ed1" }}>
                            {currency} {formatAmount(combinedVisibleAmount)}
                        </div>
                    </div>
                </Col>
                <Col span={12}>
                    <div style={{ marginBottom: 8 }}>
                        <span style={{ color: "#8c8c8c", fontSize: 12 }}>参照金额（不计入合计）</span>
                        <div style={{ fontSize: 14, fontWeight: 500, color: "#8c8c8c" }}>
                            {currency} {formatAmount(referenceAmount)}
                        </div>
                    </div>
                </Col>
                <Col span={12}>
                    <div style={{ marginBottom: 8 }}>
                        <span style={{ color: "#8c8c8c", fontSize: 12 }}>未匹配费用</span>
                        <div style={{ fontSize: 14, fontWeight: 500, color: hasUnmatched ? "#d48806" : "#8c8c8c" }}>
                            {currency} {formatAmount(summary.unmatched_amount)} / {summary.unmatched_count || 0} 项
                        </div>
                    </div>
                </Col>
                <Col span={24}>
                    <div>
                        <span style={{ color: "#8c8c8c", fontSize: 12 }}>日期范围</span>
                        <div style={{ fontSize: 13 }}>
                            {summary.date_range?.start || "-"} ~ {summary.date_range?.end || "-"}
                        </div>
                    </div>
                </Col>
            </Row>
        </div>
    );

    return (
        <div className="workbench-header__amounts" aria-label="批次金额摘要">
            <AmountMetric
                label="待审"
                amount={pendingSummary.visible_amount}
                currency={currency}
                color="#1677ff"
                suffix={`${summary.pending_invoice_count || 0}张`}
            />

            <AmountMetric
                label="已入库"
                amount={committedSummary.visible_amount}
                currency={currency}
                color="#389e0d"
                suffix={`${summary.committed_invoice_count || 0}张`}
            />

            <AmountMetric
                label="本批合计"
                amount={combinedVisibleAmount}
                currency={currency}
                color="#722ed1"
            />

            <Popover content={statsContent} title="统计详情" placement="bottomRight" trigger="click">
                <Button type="link" size="small" className="workbench-header__stats-detail" aria-label="查看统计详情">
                    <InfoCircleOutlined />
                </Button>
            </Popover>
        </div>
    );
};

// ---- 主组件 ----
const WorkbenchHeader = ({ onOpenProductMaintenance, onOpenPromptMaintenance, onOpenBillingRulesMaintenance, onOpenAgentSources, onOpenUploadDrawer, onEditBatchConfig }) => {
    const { batchNumber, vendorName, standardCostType, airportCode, currency, loading, matrixLoading, refreshAll, excludeSources, summaryViewMode } = useBatchContext();
    const { hasPermission } = useAuth();
    const canEditBatchConfig = hasPermission("settlement_ap_invoice_data_source:update");
    const canUpload = hasPermission("settlement_ap_invoice_data_source:create");

    if (loading) {
        return (
            <div className="workbench-header workbench-header--loading">
                <Spin size="small" />
                <Text type="secondary" style={{ marginLeft: 8 }}>
                    加载批次信息...
                </Text>
            </div>
        );
    }

    const costTypeLabel = standardCostType ? getStandardCostTypeLabel(standardCostType) : "—";
    const moreMenuItems = [
        onOpenProductMaintenance && {
            key: "product-maintenance",
            icon: <AppstoreOutlined />,
            label: "产品维护",
            onClick: onOpenProductMaintenance,
        },
        onOpenPromptMaintenance && {
            key: "prompt-maintenance",
            icon: <FileTextOutlined />,
            label: "提示词维护",
            onClick: onOpenPromptMaintenance,
        },
        onOpenBillingRulesMaintenance && {
            key: "billing-rules",
            icon: <BookOutlined />,
            label: "计费规则",
            onClick: onOpenBillingRulesMaintenance,
        },
        onOpenAgentSources && {
            key: "agent-sources",
            icon: <EyeOutlined />,
            label: excludeSources.length > 0 ? `验证来源（${excludeSources.length} 已隐藏）` : "验证来源",
            onClick: onOpenAgentSources,
        },
    ].filter(Boolean);

    return (
        <div className="workbench-header">
            <div className="workbench-header__top-row">
                <div className="workbench-header__identity">
                    <Tooltip title={canEditBatchConfig ? "点击修改批次号" : batchNumber}>
                        <span className="workbench-header__batch" style={canEditBatchConfig ? { cursor: "pointer" } : undefined} onClick={canEditBatchConfig ? () => onEditBatchConfig?.("batch_number") : undefined}>
                            <NumberOutlined className="workbench-header__icon" />
                            <Text strong className="workbench-header__batch-number">{batchNumber || "—"}</Text>
                            {canEditBatchConfig && <EditOutlined style={{ fontSize: 11, color: "#bfbfbf" }} />}
                        </span>
                    </Tooltip>

                    <Tooltip title={vendorName || "—"}>
                        <Text className="workbench-header__vendor">{vendorName || "—"}</Text>
                    </Tooltip>

                    <Tag color="blue" style={{ margin: 0 }}>{costTypeLabel}</Tag>

                    {airportCode ? (
                        <Tooltip title={canEditBatchConfig ? "点击修改关联机场" : airportCode}>
                            <span className="workbench-header__airport" style={canEditBatchConfig ? { cursor: "pointer" } : undefined} onClick={canEditBatchConfig ? () => onEditBatchConfig?.("airport_code") : undefined}>
                                <Text>{airportCode}</Text>
                                {canEditBatchConfig && <EditOutlined style={{ fontSize: 11, color: "#bfbfbf" }} />}
                            </span>
                        </Tooltip>
                    ) : canEditBatchConfig ? (
                        <Tooltip title="点击设置关联机场">
                            <Button type="link" size="small" style={{ padding: 0, height: "auto", color: "#bfbfbf" }} onClick={() => onEditBatchConfig?.("airport_code")}>
                                设置机场
                            </Button>
                        </Tooltip>
                    ) : null}

                    {currency && <Tag color="default">{currency}</Tag>}

                    {summaryViewMode && (
                        <Tooltip title={`汇总模式：${summaryViewMode}`}>
                            <InfoCircleOutlined className="workbench-header__mode-icon" />
                        </Tooltip>
                    )}
                </div>

                <Space size={8} className="workbench-header__actions">
                    {canUpload && onOpenUploadDrawer && (
                        <Button type="primary" size="small" icon={<UploadOutlined />} onClick={onOpenUploadDrawer}>
                            上传账单
                        </Button>
                    )}
                    <Tooltip title="刷新全部数据">
                        <Button size="small" icon={<ReloadOutlined />} onClick={refreshAll} loading={matrixLoading}>
                            刷新
                        </Button>
                    </Tooltip>
                    {moreMenuItems.length > 0 && (
                        <Dropdown menu={{ items: moreMenuItems }} trigger={["click"]}>
                            <Button size="small" icon={<MoreOutlined />}>
                                更多
                            </Button>
                        </Dropdown>
                    )}
                </Space>
            </div>

            <div className="workbench-header__signal-row">
                <StatusSignals />
                <StatsRow />
            </div>

        </div>
    );
};

export default WorkbenchHeader;
